import type { ReactElement } from "react";
import { useEffect } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { FlexRow, IconButton } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { EXTENSION_LINK } from "app/pages/sales-intelligence/constants/common";
import ShadowOnScrollWrapper from "app/pages/sales-intelligence/modules/onboarding/components/ShadowOnScrollWrapper/ShadowOnScrollWrapper";
import type { OnboardingFormEditableItems } from "app/pages/sales-intelligence/modules/onboarding/hooks/useOnboardingFormState";
import useOnboardingFormState from "app/pages/sales-intelligence/modules/onboarding/hooks/useOnboardingFormState";
import type { IActivationAgentFormDTO } from "app/pages/sales-intelligence/modules/onboarding/types/form-value";
import downloadExtensionTrackingService from "app/pages/sales-intelligence/services/tracking/download-extension/trackingService";
import { AssetsService } from "app/services/AssetsService";

import MultiSelectChipsGroup from "../components/MultiSelectChips/MultiSelectChipsGroup";
import { JOB_POSITION_OTHER } from "../constants";
import { ONBOARDING_FOOTER_HEIGHT } from "../constants/sizes";
import activationAgentTrackingService from "../services/tracking/trackingService";

import { JobPositionContainer } from "./JobPositionContainer/JobPositionContainer";
import {
    StyledExtensionBlock,
    StyledExtensionDemoImage,
    StyledFlexFooterWrapper,
    StyledFlexFormContentWrapper,
    StyledOnboardingContentContainer,
    StyledOnboardingContentSubSection,
} from "./styles";

interface IProps {
    isInfoDataFetching: boolean;
    isSignalsInfoFetching?: boolean;
    initialInfoData: OnboardingInitData | undefined;
    signalsInfoData: OnboardingSignalsInfoData | undefined;

    onFormChange: (formDto: IActivationAgentFormDTO) => void;
    onSubmit: (formDto: IActivationAgentFormDTO) => void;
}

const OnboardingContentContainer = ({
    isInfoDataFetching,
    isSignalsInfoFetching,
    initialInfoData,
    signalsInfoData,
    onFormChange,
    onSubmit,
}: IProps): ReactElement | null => {
    const { formValue, handleSetFormValue, formItems, handleSetFormItems } = useOnboardingFormState(
        initialInfoData,
        signalsInfoData,
    );

    const getFormDTO = (): IActivationAgentFormDTO => ({
        ...formValue,
        signals: formValue.signals.map((signalItem) => signalItem.definition),
    });

    useEffect(() => {
        onFormChange(getFormDTO());
    }, [formValue]);

    const handleOnChangeJobPosition = (jobTitle: string, isCustomJobPosition: boolean): void => {
        activationAgentTrackingService.trackRoleChange(
            isCustomJobPosition ? JOB_POSITION_OTHER : jobTitle,
            isCustomJobPosition ? jobTitle : undefined,
        );

        handleSetFormValue({
            jobTitle,
        });
    };

    useEffect(() => {
        if (!initialInfoData?.user?.jobTitle || formValue.jobTitle) {
            return;
        }

        handleSetFormValue({
            jobTitle: initialInfoData?.user?.jobTitle,
        });
    }, [initialInfoData?.user?.jobTitle]);

    const handleOnSelectPersonaTitles = (selectedItems: string[], clickedItem: string, isSelected: boolean): void => {
        if (isSelected) {
            activationAgentTrackingService.trackSelectPersona(clickedItem);
        }

        handleSetFormValue({ targetPersonas: selectedItems });
    };

    const handleOnSelectCompanyTypes = (selectedItems: string[], clickedItem: string, isSelected: boolean): void => {
        if (isSelected) {
            activationAgentTrackingService.trackSelectType(clickedItem);
        }

        handleSetFormValue({ targetCompanyTypes: selectedItems });
    };

    const handleAddNewItem = (itemKey: keyof OnboardingFormEditableItems, newItemValue: string): void => {
        const formItemsSet = new Set([...formItems[itemKey], newItemValue]);

        handleSetFormValue({ [itemKey]: [...formValue[itemKey], newItemValue] });

        handleSetFormItems({
            [itemKey]: new Array(...formItemsSet),
        });
    };

    const handleOnAddNewPersona = (itemLabel: string): void => {
        activationAgentTrackingService.trackAddPersona(itemLabel);

        handleAddNewItem("targetPersonas", itemLabel);
    };

    const handleAddNewTargetCompanyType = (itemLabel: string): void => {
        activationAgentTrackingService.trackAddCompanyType(itemLabel);

        handleAddNewItem("targetCompanyTypes", itemLabel);
    };

    const handleSubmitForm = (): void => {
        const formDto = getFormDTO();

        onSubmit(formDto);
    };

    const onClickExtensionTracking = () => downloadExtensionTrackingService.trackDownloadExtensionAction("onboarding");

    return (
        <StyledOnboardingContentContainer>
            <ShadowOnScrollWrapper height={`calc(100% - ${ONBOARDING_FOOTER_HEIGHT}px)`}>
                <StyledFlexFormContentWrapper
                    flexDirection="column"
                    gap="48px"
                >
                    <Flex
                        flexDirection="column"
                        gap="16px"
                    >
                        <JobPositionContainer
                            data={initialInfoData}
                            isLoading={isInfoDataFetching}
                            onChange={handleOnChangeJobPosition}
                        />
                        <MultiSelectChipsGroup
                            isLoading={isInfoDataFetching}
                            addButtonTooltip="Add title"
                            items={formItems.targetPersonas}
                            selectedItems={formValue.targetPersonas}
                            onSelectItems={handleOnSelectPersonaTitles}
                            onAddItem={handleOnAddNewPersona}
                        />
                    </Flex>

                    <StyledOnboardingContentSubSection>
                        <Text fontVariant="h6">{translate("si.onboarding.content.company-types.title")}</Text>
                        <MultiSelectChipsGroup
                            isLoading={isInfoDataFetching}
                            addButtonTooltip="Add other"
                            items={formItems.targetCompanyTypes}
                            selectedItems={formValue.targetCompanyTypes}
                            onSelectItems={handleOnSelectCompanyTypes}
                            onAddItem={handleAddNewTargetCompanyType}
                        />
                    </StyledOnboardingContentSubSection>
                    <FlexRow>
                        <StyledExtensionBlock>
                            <Text
                                fontVariant="body2"
                                color={colorsPalettes.carbon[400]}
                            >
                                {translate("si.onboarding.extension.description")}
                            </Text>
                            <a
                                target="_blank"
                                href={EXTENSION_LINK}
                                rel="noreferrer"
                                onClick={onClickExtensionTracking}
                            >
                                <IconButton
                                    type="primary"
                                    iconName="open-new-window"
                                    iconSize="12"
                                    placement="right"
                                >
                                    {translate("si.components.extension_banner.button")}
                                </IconButton>
                            </a>
                        </StyledExtensionBlock>
                        <Flex>
                            <StyledExtensionDemoImage
                                src={AssetsService.assetUrl(`/images/sales-intelligence/extension_demo.svg`)}
                                alt="Download extension"
                            />
                        </Flex>
                    </FlexRow>
                </StyledFlexFormContentWrapper>
            </ShadowOnScrollWrapper>

            <StyledFlexFooterWrapper justifyContent="end">
                <IconButton
                    type="flat"
                    isDisabled={isInfoDataFetching || isSignalsInfoFetching}
                    onClick={handleSubmitForm}
                    iconName="arrow-right"
                    placement="right"
                >
                    {translate("si.common.go-to-platform")}
                </IconButton>
            </StyledFlexFooterWrapper>
        </StyledOnboardingContentContainer>
    );
};

export default OnboardingContentContainer;
